<template>
    <div class="imagem-container">
      <!-- Loading -->
      <v-skeleton-loader 
        v-if="loading" 
        type="image" 
        height="200"
        class="rounded"
      />
      
      <!-- Imagem -->
      <div v-else-if="imagemBase64" class="imagem-wrapper">
        <v-img
          :src="imagemSrc"
          :alt="alt"
          height="200"
          cover
          class="rounded cursor-pointer"
          @click="abrirModal"
        >
          <template v-slot:placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular indeterminate color="grey-lighten-5" />
            </v-row>
          </template>
          
          <!-- Overlay com informações -->
          <template v-slot:default>
            <div class="overlay-info">
              <v-chip 
                v-if="mostrarInfo" 
                size="small" 
                color="primary" 
                class="ma-1"
              >
                {{ formatoImagem }}
              </v-chip>
            </div>
          </template>
        </v-img>
        
        <!-- Botão para ampliar -->
        <v-btn
          icon="mdi-magnify-plus"
          size="small"
          color="primary"
          class="ampliar-btn"
          @click="abrirModal"
        />
      </div>
      
      <!-- Sem imagem -->
      <div v-else class="sem-imagem">
        <v-icon size="64" color="grey-lighten-2">mdi-image-off</v-icon>
        <p class="text-caption text-grey mt-2">{{ mensagemSemImagem }}</p>
      </div>
  
      <!-- Modal para ampliar imagem -->
      <v-dialog v-model="modalAberto" max-width="900" persistent>
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center bg-primary">
            <span class="text-white">{{ alt }}</span>
            <div class="d-flex align-center">
              <!-- Botões de ação -->
              <v-btn 
                icon="mdi-download" 
                variant="text" 
                color="white"
                @click="baixarImagem"
                class="me-2"
              />
              <v-btn 
                icon="mdi-fullscreen" 
                variant="text" 
                color="white"
                @click="toggleFullscreen"
                class="me-2"
              />
              <v-btn 
                icon="mdi-close" 
                variant="text" 
                color="white"
                @click="fecharModal" 
              />
            </div>
          </v-card-title>
          
          <v-card-text class="pa-0 position-relative">
            <v-img
              :src="imagemSrc"
              :alt="alt"
              :max-height="alturaModal"
              contain
              class="modal-image"
              @click="toggleZoom"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="primary" size="64" />
                </v-row>
              </template>
            </v-img>
            
            <!-- Controles de zoom -->
            <div class="zoom-controls">
              <v-btn-group variant="elevated" density="compact">
                <v-btn 
                  icon="mdi-minus" 
                  size="small"
                  @click="diminuirZoom"
                  :disabled="zoomLevel <= 0.5"
                />
                <v-btn 
                  size="small"
                  @click="resetZoom"
                  min-width="60"
                >
                  {{ Math.round(zoomLevel * 100) }}%
                </v-btn>
                <v-btn 
                  icon="mdi-plus" 
                  size="small"
                  @click="aumentarZoom"
                  :disabled="zoomLevel >= 3"
                />
              </v-btn-group>
            </div>
          </v-card-text>
          
          <!-- Informações da imagem -->
          <v-card-actions v-if="mostrarDetalhes" class="bg-grey-lighten-4">
            <v-row no-gutters class="w-100">
              <v-col cols="12" sm="4">
                <v-chip size="small" variant="outlined">
                  <v-icon start>mdi-file-image</v-icon>
                  {{ formatoImagem }}
                </v-chip>
              </v-col>
              <v-col cols="12" sm="4">
                <v-chip size="small" variant="outlined">
                  <v-icon start>mdi-resize</v-icon>
                  {{ tamanhoImagem }}
                </v-chip>
              </v-col>
              <v-col cols="12" sm="4">
                <v-chip size="small" variant="outlined">
                  <v-icon start>mdi-magnify</v-icon>
                  {{ Math.round(zoomLevel * 100) }}%
                </v-chip>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </template>
  
  <script>
  // ✅ REMOVIDO O IMPORT DO aparelhoService - não é necessário aqui
  
  export default {
    name: 'ImagemTipoAparelho',
    props: {
      imagemBase64: {
        type: String,
        default: null
      },
      alt: {
        type: String,
        default: 'Imagem do aparelho'
      },
      loading: {
        type: Boolean,
        default: false
      },
      mostrarInfo: {
        type: Boolean,
        default: false
      },
      mostrarDetalhes: {
        type: Boolean,
        default: true
      },
      mensagemSemImagem: {
        type: String,
        default: 'Sem imagem disponível'
      },
      alturaModal: {
        type: [String, Number],
        default: 600
      }
    },
    data() {
      return {
        modalAberto: false,
        zoomLevel: 1,
        fullscreen: false,
        imagemCarregada: false
      }
    },
    computed: {
      imagemSrc() {
        if (!this.imagemBase64) return null
        
        // Detectar formato da imagem com mais precisão
        let mimeType = this.detectarMimeType(this.imagemBase64)
        
        return `data:${mimeType};base64,${this.imagemBase64}`
      },
      
      formatoImagem() {
        if (!this.imagemBase64) return 'N/A'
        
        const mimeType = this.detectarMimeType(this.imagemBase64)
        return mimeType.split('/')[1].toUpperCase()
      },
      
      tamanhoImagem() {
        if (!this.imagemBase64) return 'N/A'
        
        // Calcular tamanho aproximado em KB
        const tamanhoBytes = (this.imagemBase64.length * 3) / 4
        const tamanhoKB = Math.round(tamanhoBytes / 1024)
        
        if (tamanhoKB > 1024) {
          return `${(tamanhoKB / 1024).toFixed(1)} MB`
        }
        return `${tamanhoKB} KB`
      }
    },
    watch: {
      modalAberto(novoValor) {
        if (!novoValor) {
          this.resetZoom()
          this.fullscreen = false
        }
      }
    },
    methods: {
      detectarMimeType(base64String) {
        // Detectar tipo MIME baseado nos primeiros bytes
        const signatures = {
          'iVBORw0KGgo': 'image/png',
          '/9j/': 'image/jpeg',
          'R0lGODlh': 'image/gif',
          'R0lGODdh': 'image/gif',
          'UklGR': 'image/webp',
          'Qk': 'image/bmp'
        }
        
        for (const [signature, mimeType] of Object.entries(signatures)) {
          if (base64String.startsWith(signature)) {
            return mimeType
          }
        }
        
        // Padrão para JPEG se não conseguir detectar
        return 'image/jpeg'
      },
      
      abrirModal() {
        this.modalAberto = true
        this.resetZoom()
      },
      
      fecharModal() {
        this.modalAberto = false
        this.resetZoom()
        this.fullscreen = false
      },
      
      toggleZoom() {
        if (this.zoomLevel === 1) {
          this.zoomLevel = 2
        } else {
          this.resetZoom()
        }
      },
      
      aumentarZoom() {
        if (this.zoomLevel < 3) {
          this.zoomLevel = Math.min(3, this.zoomLevel + 0.25)
        }
      },
      
      diminuirZoom() {
        if (this.zoomLevel > 0.5) {
          this.zoomLevel = Math.max(0.5, this.zoomLevel - 0.25)
        }
      },
      
      resetZoom() {
        this.zoomLevel = 1
      },
      
      toggleFullscreen() {
        this.fullscreen = !this.fullscreen
        // Implementar lógica de fullscreen se necessário
      },
      
      baixarImagem() {
        if (!this.imagemSrc) return
        
        try {
          const link = document.createElement('a')
          link.href = this.imagemSrc
          link.download = `${this.alt.replace(/\s+/g, '_')}.${this.formatoImagem.toLowerCase()}`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          this.$emit('imagem-baixada', {
            alt: this.alt,
            formato: this.formatoImagem,
            tamanho: this.tamanhoImagem
          })
        } catch (error) {
          console.error('Erro ao baixar imagem:', error)
          this.$emit('erro-download', error)
        }
      }
    }
  }
  </script>
  
  <style scoped>
  .imagem-container {
    position: relative;
    width: 100%;
  }
  
  .imagem-wrapper {
    position: relative;
    overflow: hidden;
    border-radius: 8px;
  }
  
  .ampliar-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    background-color: rgba(0, 0, 0, 0.7) !important;
    backdrop-filter: blur(4px);
    transition: all 0.3s ease;
  }
  
  .ampliar-btn:hover {
    background-color: rgba(0, 0, 0, 0.9) !important;
    transform: scale(1.1);
  }
  
  .overlay-info {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 2;
  }
  
  .sem-imagem {
    height: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 2px dashed #e0e0e0;
    border-radius: 8px;
    background-color: #fafafa;
    transition: all 0.3s ease;
  }
  
  .sem-imagem:hover {
    border-color: #bdbdbd;
    background-color: #f5f5f5;
  }
  
  .cursor-pointer {
    cursor: pointer;
  }
  
  .modal-image {
    transition: transform 0.3s ease;
    cursor: zoom-in;
  }
  
  .modal-image:hover {
    cursor: zoom-in;
  }
  
  .zoom-controls {
    position: absolute;
    bottom: 16px;
    right: 16px;
    z-index: 10;
  }
  
  .position-relative {
    position: relative;
  }
  
  /* Animações */
  .imagem-wrapper:hover .ampliar-btn {
    opacity: 1;
  }
  
  .ampliar-btn {
    opacity: 0.8;
    transition: opacity 0.3s ease;
  }
  
  /* Responsividade */
  @media (max-width: 600px) {
    .zoom-controls {
      bottom: 8px;
      right: 8px;
    }
    
    .ampliar-btn {
      top: 4px;
      right: 4px;
    }
    
    .overlay-info {
      top: 4px;
      left: 4px;
    }
  }
  </style>